import { useCallback, useEffect, useRef, useState } from "react";
import { listStaff, createStaff, updateStaff, deleteStaff, type Staff } from "../services/staff";
import { useLang } from "../hooks/useLang";
import { useAuth } from "../auth/AuthContext";
import Icon from "./icons";

// StaffPanel — müşterinin panel kullanıcıları (personel). Yönetim sayfasının
// "Personel" tab'ında render edilir. Tek bir settings-section fragment döner.
export default function StaffPanel() {
  const { t } = useLang();
  const { user } = useAuth();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffErr, setStaffErr] = useState<string | null>(null);
  const [newStaffEmail, setNewStaffEmail] = useState("");
  const [newStaffPassword, setNewStaffPassword] = useState("");
  const [newStaffName, setNewStaffName] = useState("");
  const [staffCreating, setStaffCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  // Modal kapanınca odak "Ekle" butonuna geri döner; klavye kullanıcısı
  // listenin başına fırlamaz.
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    setStaffErr(null);
    try {
      setStaff(await listStaff());
    } catch (e) {
      setStaffErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStaffLoading(false);
    }
  }, []);

  useEffect(() => { void loadStaff(); }, [loadStaff]);

  const onAddStaff = useCallback(async () => {
    if (!newStaffEmail.trim() || newStaffPassword.length < 6) return;
    setStaffCreating(true);
    setStaffErr(null);
    try {
      await createStaff({ email: newStaffEmail.trim(), password: newStaffPassword, full_name: newStaffName.trim() });
      setNewStaffEmail(""); setNewStaffPassword(""); setNewStaffName("");
      await loadStaff();
      setModalOpen(false);
    } catch (e) {
      setStaffErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStaffCreating(false);
    }
  }, [newStaffEmail, newStaffPassword, newStaffName, loadStaff]);

  const onToggleStaff = useCallback(async (st: Staff) => {
    setStaffErr(null);
    try {
      await updateStaff(st.id, { is_active: !st.is_active });
      await loadStaff();
    } catch (e) {
      setStaffErr(e instanceof Error ? e.message : String(e));
    }
  }, [loadStaff]);

  const onDeleteStaff = useCallback(async (st: Staff) => {
    if (!confirm(t("staff_delete_confirm").replace("{name}", st.email))) return;
    setStaffErr(null);
    try {
      await deleteStaff(st.id);
      await loadStaff();
    } catch (e) {
      setStaffErr(e instanceof Error ? e.message : String(e));
    }
  }, [loadStaff, t]);

  const canAddStaff = !!newStaffEmail.trim() && newStaffPassword.length >= 6;

  const openModal = useCallback(() => {
    setStaffErr(null);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Modal acikken Escape kapatir (klavye kullanicisi fareye uzanmak zorunda
  // kalmasin) ve ilk alan odaklanir.
  useEffect(() => {
    if (!modalOpen) return;
    firstFieldRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeModal]);

  return (
    <section className="settings-section">
      {/*
        Eskiden üç girdi + buton liste ÜSTÜNDE yan yana duruyordu; sayfa açılır
        açılmaz karşılayan şey "kullanıcı ekle" formuydu, oysa asıl iş mevcut
        kullanıcıları görmek. Form modala alındı, liste öne çıktı
        (müşteri talebi 2026-07-30).
      */}
      <div className="section-head" style={{ marginBottom: 12 }}>
        <div>
          <h3>{t("staff_section_title")}</h3>
          <p className="hint" style={{ margin: "4px 0 0" }}>{t("staff_section_hint")}</p>
        </div>
        <button ref={triggerRef} className="btn-pri" onClick={openModal}>
          <Icon name="plus" size={15} /> {t("staff_add_btn")}
        </button>
      </div>

      {staffErr && !modalOpen && <div className="error" style={{ marginBottom: 12 }}>{staffErr}</div>}

      {staffLoading && <div className="hint">{t("staff_loading")}</div>}
      {!staffLoading && staff.length === 0 && <div className="hint">{t("staff_empty")}</div>}

      <div className="staff-list" style={{ display: "grid", gap: 8 }}>
        {staff.map((st) => {
          const isSelf = user?.id === st.id;
          return (
            <div key={st.id} style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 6,
            }}>
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {st.full_name || st.email} {isSelf && <span className="hint">{t("staff_you")}</span>}
                </div>
                <div className="hint" style={{ fontSize: 12 }}>{st.email}</div>
              </div>
              <span className={"badge " + (st.is_active ? "ok" : "off")}>
                {st.is_active ? t("staff_active") : t("staff_passive")}
              </span>
              {/* KENDI SATIRINDA EYLEM HIC CIZILMEZ (2026-08-17).
                  Once butonlar `disabled={isSelf}` ile duruyordu ve sunucu da
                  reddediyordu (staff.go Delete/Patch → 400), ama kullanici
                  onlari tiklanabilir saniyordu: index.css'te .btn-sec:disabled
                  IKI KEZ tanimli ve SONRAKI kural kazaniyor —
                  `cursor: progress; opacity: .65`. Yani yasakli buton "mesgul"
                  gibi gorunuyor, "yapilamaz" gibi degil.
                  Pasif buton yerine eylemi hic sunmamak hem bu stil
                  belirsizligini atlatir hem NEDEN yapilamadigini soyler. */}
              {isSelf ? (
                <span className="hint" style={{ fontSize: 12 }}>{t("staff_self_locked")}</span>
              ) : (
                <>
                  <button className="btn-sec" onClick={() => onToggleStaff(st)}>
                    {st.is_active ? t("staff_deactivate") : t("staff_activate")}
                  </button>
                  <button className="btn-sec" onClick={() => onDeleteStaff(st)}>
                    {t("delete")}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {modalOpen && (
        <div
          className="fm-overlay"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="fm" role="dialog" aria-modal="true" aria-labelledby="staff-add-title">
            <div className="fm-head">
              <h4 id="staff-add-title">{t("staff_add_btn")}</h4>
              <button type="button" className="fm-close" onClick={closeModal} aria-label={t("close")}>
                <Icon name="x" size={18} />
              </button>
            </div>

            {/* form: Enter tuşu kaydeder — modalda tek başına doğal davranış */}
            <form onSubmit={(e) => { e.preventDefault(); void onAddStaff(); }}>
              <div className="fm-body">
                {staffErr && <div className="error" style={{ marginBottom: 10 }}>{staffErr}</div>}

                <label className="form-row">
                  <span>{t("staff_name_placeholder")}</span>
                  <input
                    ref={firstFieldRef}
                    type="text"
                    value={newStaffName}
                    onChange={(e) => setNewStaffName(e.target.value)}
                  />
                </label>
                <label className="form-row">
                  <span>{t("staff_email_placeholder")}</span>
                  <input
                    type="email"
                    value={newStaffEmail}
                    onChange={(e) => setNewStaffEmail(e.target.value)}
                    autoComplete="off"
                    required
                  />
                </label>
                <label className="form-row">
                  <span>{t("staff_password_placeholder")}</span>
                  <input
                    type="password"
                    value={newStaffPassword}
                    onChange={(e) => setNewStaffPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={6}
                    required
                  />
                </label>
              </div>

              <div className="fm-foot">
                <button type="button" className="btn-sec" onClick={closeModal}>{t("cancel")}</button>
                <button type="submit" className="btn-pri" disabled={staffCreating || !canAddStaff}>
                  {staffCreating ? t("saving") : t("staff_add_btn")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
